import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { logEvent } from 'firebase/analytics';
import { analytics } from '@/services/firebase';
import useAuthStore from '@/stores/authStore';

// Layout Components
import { Navigation, Footer } from '@components';

// Auth Pages
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AuthCallback from '@/pages/AuthCallback';

// Public Pages
import Home from '@/pages/Home';
import Blogs from '@/pages/Blogs';
import BlogDetail from '@/pages/BlogDetail';
import News from '@/pages/News';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import WorkWithMe from '@/pages/WorkWithMe';

// Protected Pages
import Projects from '@/pages/Projects';
import Posts from '@/pages/Posts';
import Analytics from '@/pages/Analytics';
import Calendar from '@/pages/Calendar';
import Profile from '@/pages/Profile';
import Settings from '@/pages/Settings';

// Utils
import ProtectedRoute from '@/utils/ProtectedRoute';
import AdminRoute from '@/utils/AdminRoute';

// Admin Pages
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminPosts from '@/pages/admin/AdminPosts';
import AdminPostEditor from '@/pages/admin/AdminPostEditor';
import AdminProjects from '@/pages/admin/AdminProjects';
import AdminProjectEditor from '@/pages/admin/AdminProjectEditor';
import AdminPricing from '@/pages/admin/AdminPricing';
import AdminPricingEditor from '@/pages/admin/AdminPricingEditor';
import AdminFeatures from '@/pages/admin/AdminFeatures';
import AdminFeatureEditor from '@/pages/admin/AdminFeatureEditor';
import AdminHero from '@/pages/admin/AdminHero';
import AdminSocial from '@/pages/admin/AdminSocial';
import AdminSocialEditor from '@/pages/admin/AdminSocialEditor';
import AdminContentLoop from '@/pages/admin/AdminContentLoop';
import AdminLeads from '@/pages/admin/AdminLeads';
import AdminAnalytics from '@/pages/admin/analytics/AdminAnalytics';
import AdminGrowth from '@/pages/admin/analytics/AdminGrowth';

// Tracks page views on every React Router navigation
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
      });
    }
  }, [location]);
  return null;
}

function App() {
  const { initialize } = useAuthStore();

  // Initialize auth state on app load
  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <PageViewTracker />
      <div className="flex flex-col min-h-screen">
        <Navigation />
        
        <main className="flex-1">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Blog Routes - Public */}
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/:id" element={<BlogDetail />} />

            {/* News Route - Public */}
            <Route path="/news" element={<News />} />

            {/* Consulting funnel - Public (GOAL.md pillar 6) */}
            <Route path="/work-with-me" element={<WorkWithMe />} />

            {/* Protected Routes */}
            <Route
              path="/projects"
              element={
                <AdminRoute>
                  <Projects />
                </AdminRoute>
              }
            />
            <Route
              path="/posts"
              element={
                <ProtectedRoute>
                  <Posts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <AdminRoute>
                  <Analytics />
                </AdminRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <AdminRoute>
                  <Calendar />
                </AdminRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />

            {/* Legal Routes */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />

            {/* 404 Redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="hero" element={<AdminHero />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="posts" element={<AdminPosts />} />
              <Route path="posts/new" element={<AdminPostEditor />} />
              <Route path="posts/:id/edit" element={<AdminPostEditor />} />
              <Route path="projects" element={<AdminProjects />} />
              <Route path="projects/new" element={<AdminProjectEditor />} />
              <Route path="projects/:id/edit" element={<AdminProjectEditor />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="pricing/new" element={<AdminPricingEditor />} />
              <Route path="pricing/:id/edit" element={<AdminPricingEditor />} />
              <Route path="features" element={<AdminFeatures />} />
              <Route path="features/new" element={<AdminFeatureEditor />} />
              <Route path="features/:id/edit" element={<AdminFeatureEditor />} />
              <Route path="social" element={<AdminSocial />} />
              <Route path="social/new" element={<AdminSocialEditor />} />
              <Route path="social/:id/edit" element={<AdminSocialEditor />} />
              <Route path="content-loop" element={<AdminContentLoop />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="growth" element={<AdminGrowth />} />
            </Route>
          </Routes>
        </main>

        <Footer />
      </div>

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#1E3A8A',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
          success: {
            iconTheme: {
              primary: '#14B8A6',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </Router>
  );
}

export default App;
